import React,{useEffect,useMemo,useState} from 'react'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'
import moment from 'moment';
import DataTable from 'react-data-table-component';

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import PlayerCard from '../ui/PlayerCard'
import Avatar from '../ui/Avatar'
import './lists.css'

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconOpen=()=>(
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 20h9"/>
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
    </svg>
)

const IconX=()=>(
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS
   react-data-table-component styles through a JS object, so the table
   cannot read CSS variables on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference changes.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      /* read after the new custom properties have landed */
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

const plural=(n,one,many)=>(n===1 ? one : many)

const orientationOf=(value)=>(
    value==1 ? 'Right' : value==2 ? 'Left' : 'Both Left & Right'
)

/* the multi-select stores {label,value}; fall back to the raw id if a label is missing */
const labelsOf=(items)=>(
    Array.isArray(items)
        ? items.map(item=>(item && item.label!==undefined && item.label!==null ? item.label : String(item && item.value!==undefined ? item.value : item)))
        : []
)

export default function PlayersList() {
    const [lists,setLists]=useState([])
    const [countries,setCountries]=useState([])//useState(JSON.parse(sessionStorage.getItem('appCountries')))
    const [positions,setPositions]=useState([])
    const [queryRsults,setQueryResults]=useState([])
    /* true from the first paint: loadLists() fires on mount, so an empty-state
       flash before the request starts would be a lie */
    const [isLoading,setIsLoading]=useState(true)
    const [a1,setA1]=useState(0)
    const [a2,setA2]=useState(0)
    const [h1,setH1]=useState(0)
    const [h2,setH2]=useState(0)
    const [w1,setW1]=useState(0)
    const [w2,setW2]=useState(0)
    const [o,setO]=useState(0)
    const [s,setS]=useState('')
    const [listName,setListName]=useState('')
    const [sqlCriteria,setSqlCriteria]=useState('')

    /* screen state — this is what replaced the alert() calls */
    const [selected,setSelected]=useState('0')
    /* bumped on every successful criteria load so re-running the SAME list still
       fires the effect below — without it, picking a list twice (or two lists
       that share a name) left the spinner up forever. */
    const [runToken,setRunToken]=useState(0)
    const [note,setNote]=useState(null)
    const [failed,setFailed]=useState(false)
    const [listsFailed,setListsFailed]=useState(false)

    const navigate=useNavigate()
    const tokens=useThemeTokens()

    useEffect(()=>{
        if (listName.length>0){
            createSQL()
        }else{
            setQueryResults([])
        }

    },[listName,runToken])

    const openPlayer=(uuid)=>navigate('/user/'+uuid)

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width: '58px',
            center: true
        },
        {
            name: '',
            cell: row => (
                <PlayerCard
                    uuid={row.UUID}
                    name={row.FirstName+' '+row.LastName}
                    position={row.Position}
                    points={row.Points}
                >
                    {/* 30px keeps the face inside the 58px avatar column below */}
                    <span className="pl-hold">
                        <Avatar
                            uuid={row.UUID}
                            name={row.FirstName+' '+row.LastName}
                            size={30}
                        />
                    </span>
                </PlayerCard>
            ),
            width: '58px',
            center: true
        },

        {
            name: 'Name',
            selector: (row,index) =>  row.FirstName+ ' '+row.LastName,
            cell: row => (
                <button
                    type="button"
                    className="pl-name"
                    title="Open player profile"
                    onClick={()=>openPlayer(row.UUID)}
                >
                    {row.FirstName+' '+row.LastName}
                </button>
            ),
            sortable: true,
            minWidth: '180px',
            grow: 2

        },
        {
            name: 'Age',
            selector: (row,index) =>  row.Age,
            sortable: true,
            width: '76px',
            center: true
        },
        {
            name: 'Country',
            selector: (row,index) =>  row.Country,
            sortable: true,
            minWidth: '120px'
        },
        {
            name: 'Position',
            selector: (row,index) =>  row.Position,
            sortable: true,
            minWidth: '130px'
        },
        {
            name: 'Orientation',
            selector: (row,index) =>  orientationOf(row.LeftRight),
            sortable: true,
            minWidth: '150px'
        },
        {
            name: 'Height',
            selector: (row,index) =>  row.Height,
            cell: row => <span className="pl-num">{row.Height}</span>,
            sortable: true,
            width: '92px',
            right: true
        },
        {
            name: 'Weight',
            selector: (row,index) =>  row.Weight,
            cell: row => <span className="pl-num">{row.Weight}</span>,
            sortable: true,
            width: '92px',
            right: true
        },
        {
            name: 'Team',
            selector: (row,index) =>  row.Team,
            sortable: true,
            minWidth: '150px'
        } ,
        {
            name: 'Points',
            selector: (row,index) =>  row.Points,
            cell: row => <span className="pl-num">{row.Points}</span>,
            sortable: true,
            width: '92px',
            right: true
        },
        {
            name: 'Posts',
            selector: (row,index) =>  row.Posts,
            cell: row => <span className="pl-num">{row.Posts}</span>,
            sortable: true,
            width: '86px',
            right: true
        },
        {
            name: 'Challenges',
            selector: (row,index) =>  row.Challenges,
            cell: row => <span className="pl-num">{row.Challenges}</span>,
            sortable: true,
            width: '112px',
            right: true
        },
        {
            name: '',
            cell: row => (
                <button
                    type="button"
                    className="pl-rowbtn"
                    title="Open player profile"
                    aria-label={'Open '+row.FirstName+' '+row.LastName}
                    onClick={()=>openPlayer(row.UUID)}
                >
                    <IconOpen/>
                </button>
            ),
            width: '64px',
            center: true
        },

    ]


    useEffect(()=>{
        loadLists()
      },[])

    const loadLists=()=>{
        setIsLoading(true)
        setListsFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'manager/listscriteria/lists/'+sessionStorage.getItem('userToken'),{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }
            })
          .then(res=>{
            if (res.data.err===0){
              //console.log(res.data.results)
              setLists(res.data.results)
            }else{
              setListsFailed(true)
              setNote({tone:'crit',text:'Your saved lists could not be loaded. Please try again.'})
            }

            setIsLoading(false)

          }).catch(err=>{
              setIsLoading(false)
              setListsFailed(true)
              setNote({tone:'crit',text:'Your saved lists could not be loaded. Check your connection and try again.'})
              console.log(err)
          })
      }

      const loadList= (listID)=>{
        setSelected(listID)
        setNote(null)
        setFailed(false)

        if (listID==='0'){
            setQueryResults([])
            setListName('')
        }else{
            setIsLoading(true)
            axios.get(process.env.REACT_APP_API_SERVER+'manager/listscriteria/list/'+listID,{
                    headers: {
                        'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                        }
                    })
                .then(res=>{
                    //console.log(res.data)
                    if (res.data.err===0){

                    setCountries(res.data.results.countries)
                    setPositions(res.data.results.positions)
                        setListName(res.data.results.listName)
                        setA1(res.data.results.a1)
                        setA2(res.data.results.a2)
                        setH1(res.data.results.h1)
                        setH2(res.data.results.h2)
                        setW1(res.data.results.w1)
                        setW2(res.data.results.w2)
                        setO(res.data.results.o)
                        setS(res.data.results.s)
                        setRunToken(token=>token+1)

                    }else{
                    /* the criteria never arrived, so nothing will be run */
                    setIsLoading(false)
                    setFailed(true)
                    setNote({tone:'crit',text:'That list could not be opened. Please try again.'})
                    }


                    // setTimeout(()=>{
                    //     createSQL()
                    // },1000)

                }).catch(err=>{
                    setIsLoading(false)
                    setFailed(true)
                    setNote({tone:'crit',text:'That list could not be opened. Check your connection and try again.'})
                    console.log(err)
                })
        }



      }


      const listsList=lists.map(item=>{
        return(
          <option key={item.UUID} value={item.UUID} >{item.listName}</option>
        )
      })


      const createSQL=()=>{


        var sqlCountries=''
        var sql=''
        const SportID=sessionStorage.getItem('SportID')

        countries.forEach((item,index)=>{
            if (index===0){
                sqlCountries=sqlCountries+ ' CountryIDINT='+item.value
            }else{
                sqlCountries=sqlCountries+ ' OR CountryIDINT='+item.value
            }
        })
        if (sqlCountries.length>0){
            sqlCountries='('+sqlCountries+')'
        }
        sql=sqlCountries

        var sqlPositions=''
        positions.forEach((item,index)=>{
            if (index===0){
                sqlPositions=sqlPositions+ ' Users.PositionID='+item.value
            }else{
                sqlPositions=sqlPositions+ ' OR Users.PositionID='+item.value
            }
        })
        if (sqlPositions.length>0){
            sqlPositions='('+sqlPositions+')'
        }
        if (sql.length>0 && sqlPositions.length>0){
            sql=sql+' AND '+sqlPositions
        }else{
            sql=sql+sqlPositions
        }

        var sqlHeight1=''
        if (h1>0){
            sqlHeight1=' Height>='+h1*100
        }
        if (sql.length>0 && sqlHeight1.length>0){
            sql=sql+' AND '+sqlHeight1
        }else{
            sql=sql+sqlHeight1
        }

        var sqlHeight2=''
        if (h2>0){
            sqlHeight2=' Height<='+h2*100
        }
        if (sql.length>0 && sqlHeight2.length>0){
            sql=sql+' AND '+sqlHeight2
        }else{
            sql=sql+sqlHeight2
        }


        var sqlWeight1=''
        if (w1>0){
            sqlWeight1=' Weight>='+w1
        }
        if (sql.length>0 && sqlWeight1.length>0){
            sql=sql+' AND '+sqlWeight1
        }else{
            sql=sql+sqlWeight1
        }

        var sqlWeight2=''
        if (w2>0){
            sqlWeight2=' Weight<='+w2
        }
        if (sql.length>0 && sqlWeight2.length>0){
            sql=sql+' AND '+sqlWeight2
        }else{
            sql=sql+sqlWeight2
        }


        var sqlOrientation=''
        if (o>0){
            sqlOrientation=' LeftRight='+o
        }
        if (sql.length>0 && sqlOrientation.length>0){
            sql=sql+' AND '+sqlOrientation
        }else{
            sql=sql+sqlOrientation
        }

        console.log(sql)

        setSqlCriteria(sql)

        axios.post(process.env.REACT_APP_API_SERVER+'manager/listscriteria/results',{sql,SportID},{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
                }
            })
          .then(res=>{
            let listResults=res.data.results
            let results=res.data.results
            const todayDate = moment().year()

            results.forEach(item=>{
                const dateOfBirth=moment(item.DateOfBirth).year()
                item.Age=todayDate-dateOfBirth
                //console.log(todayDate-dateOfBirth)
            })

            if (a1>0){
                listResults=listResults.filter(item=>item.Age>=a1)
                //results=[...listResults]
            }
            if (a2>0){
                listResults=listResults.filter(item=>item.Age<=a2)
                //results=[...listResults]
            }
            if (s.length>0){
                listResults=listResults.filter(item=>item.SearchStr.includes(s.toUpperCase()))
                //results=[...listResults]
            }
            //console.log(listResults)


            if (listResults.length>0){
                setQueryResults(listResults)
            }else{
                setQueryResults([])
            }
            setFailed(false)
            setIsLoading(false)
            //console.log(listResults)

          }).catch(err=>{
            /* the spinner used to hang here forever — say so instead */
            setQueryResults([])
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'The list could not be run against the player pool. Please try again.'})
            console.log(err)
          })
    }

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>{
      const t=tokens
      return {
        table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
        tableWrapper:{ style:{ display:'block' } },
        head:{ style:{ color:t['--ink-3'] } },
        headRow:{
          style:{
            backgroundColor:t['--panel-raise'],
            color:t['--ink-3'],
            minHeight:'42px',
            borderBottomWidth:'1px',
            borderBottomColor:t['--line'],
            borderBottomStyle:'solid'
          }
        },
        headCells:{
          style:{
            paddingLeft:'12px',
            paddingRight:'12px',
            fontFamily:t['--mono'],
            fontSize:'10px',
            fontWeight:600,
            letterSpacing:'0.08em',
            textTransform:'uppercase',
            color:t['--ink-3']
          }
        },
        rows:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink'],
            fontFamily:t['--sans'],
            fontSize:t['--step-0']||'0.875rem',
            minHeight:'50px',
            '&:not(:last-of-type)':{
              borderBottomWidth:'1px',
              borderBottomStyle:'solid',
              borderBottomColor:t['--line-soft']
            }
          },
          highlightOnHoverStyle:{
            backgroundColor:t['--accent-wash'],
            color:t['--ink'],
            borderBottomColor:t['--line-soft'],
            outlineWidth:'0px',
            outlineColor:t['--panel'],
            transitionDuration:'0.15s',
            transitionProperty:'background-color'
          }
        },
        cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
        pagination:{
          style:{
            backgroundColor:t['--panel'],
            color:t['--ink-3'],
            fontSize:'12px',
            minHeight:'50px',
            borderTopWidth:'1px',
            borderTopStyle:'solid',
            borderTopColor:t['--line']
          },
          pageButtonsStyle:{
            color:t['--ink-2'],
            fill:t['--ink-2'],
            backgroundColor:'transparent',
            '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
            '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
            '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
          }
        },
        noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
        progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
      }
    },[tokens])

    const hasList=selected!=='0'
    const resultCount=queryRsults.length

    /* A plain-language read of the criteria the loaded list carries. */
    const criteria=useMemo(()=>{
      if (!hasList) return []
      const chips=[]
      const countryLabels=labelsOf(countries)
      const positionLabels=labelsOf(positions)

      if (countryLabels.length>0) chips.push(['Countries',countryLabels.join(', ')])
      if (positionLabels.length>0) chips.push(['Positions',positionLabels.join(', ')])

      if (a1>0 && a2>0) chips.push(['Age',a1+'–'+a2])
      else if (a1>0) chips.push(['Age','from '+a1])
      else if (a2>0) chips.push(['Age','up to '+a2])

      if (h1>0 && h2>0) chips.push(['Height',h1+'–'+h2+' m'])
      else if (h1>0) chips.push(['Height','from '+h1+' m'])
      else if (h2>0) chips.push(['Height','up to '+h2+' m'])

      if (w1>0 && w2>0) chips.push(['Weight',w1+'–'+w2+' kg'])
      else if (w1>0) chips.push(['Weight','from '+w1+' kg'])
      else if (w2>0) chips.push(['Weight','up to '+w2+' kg'])

      if (o>0) chips.push(['Orientation',orientationOf(o)])
      if (s && s.length>0) chips.push(['Keyword',s])

      return chips
    },[hasList,countries,positions,a1,a2,h1,h2,w1,w2,o,s])

  return (
    <div className="pl-page">

      <div className="pl-head">
        <div className="pl-headline">
          <span className="mono pl-eyebrow">Scouting</span>
          <h1 className="pl-title">Players' Lists</h1>
          <p className="pl-head-sub">
            Pick one of your saved lists and its criteria run against the current player pool.
            Hover a player to preview their profile and skill ratings without leaving the results.
          </p>
        </div>
        <div className="pl-head-actions">
          <Btn onClick={()=>navigate('/lists')}>View/Edit Lists</Btn>
          <Btn variant="accent" onClick={()=>navigate('/list')}>+ Create New List</Btn>
        </div>
      </div>

      {note
        ? <div className="pl-note" data-tone={note.tone} role="alert">
            <span>{note.text}</span>
            <button type="button" className="pl-note-x" aria-label="Dismiss" onClick={()=>setNote(null)}>
              <IconX/>
            </button>
          </div>
        : null}

      <Panel
        title="Saved list"
        subtitle={
          listsFailed
            ? 'Lists unavailable'
            : lists.length>0
              ? lists.length+' '+plural(lists.length,'list','lists')+' available'
              : isLoading
                ? 'Loading…'
                : 'Nothing saved yet'
        }
      >
        {listsFailed
          ? <div className="pl-empty">
              <div className="pl-empty-t">Your lists could not be loaded</div>
              <div className="pl-empty-s">
                The server did not answer. Nothing has been lost — try loading them again.
              </div>
              <div className="pl-empty-a">
                <Btn variant="accent" onClick={loadLists}>Try again</Btn>
              </div>
            </div>
          : lists.length===0 && isLoading
            ? <div className="pl-sk" aria-hidden="true">
                <span className="skeleton"></span>
                <span className="skeleton"></span>
              </div>
            : lists.length===0
            ? <div className="pl-empty">
                <div className="pl-empty-t">No saved lists yet</div>
                <div className="pl-empty-s">
                  A list stores a set of scouting criteria — age, height, country, position —
                  so you can rerun the same search whenever the player pool changes.
                </div>
                <div className="pl-empty-a">
                  <Btn variant="accent" onClick={()=>navigate('/list')}>+ Create New List</Btn>
                </div>
              </div>
            : <>
                <div className="pl-pick">
                  <div className="pl-field">
                    <label className="mono pl-label" htmlFor="pl-list">Select list</label>
                    <select
                      id="pl-list"
                      className="pl-select"
                      value={selected}
                      onChange={(e)=>loadList(e.target.value)}
                    >
                      <option value="0">[SELECT LIST]</option>
                      {listsList}
                    </select>
                  </div>
                  <div className="pl-pick-hint">
                    Choosing a list runs it straight away. Its criteria appear below so you can
                    see exactly what is being asked for.
                  </div>
                </div>

                {hasList
                  ? <div className="pl-crit">
                      {criteria.length>0
                        ? criteria.map(([key,value])=>(
                            <span className="pl-chip" key={key} title={key+': '+value}>
                              <span className="pl-chip-k">{key}</span>
                              <span className="pl-chip-v">{value}</span>
                            </span>
                          ))
                        : <span className="pl-crit-empty">
                            This list sets no filters — it returns the whole player pool.
                          </span>}
                    </div>
                  : null}
              </>}
      </Panel>

      <Panel
        title="Results"
        subtitle={
          isLoading
            ? 'Running…'
            : !hasList
              ? 'No list selected'
              : resultCount>0
                ? resultCount+' '+plural(resultCount,'player','players')
                : 'No matches'
        }
        actions={
          hasList && !isLoading
            ? <Btn onClick={()=>loadList(selected)}>Run again</Btn>
            : null
        }
      >
        {isLoading
          ? <div className="pl-sk" aria-hidden="true">
              <span className="skeleton"></span>
              <span className="skeleton"></span>
              <span className="skeleton"></span>
              <span className="skeleton"></span>
              <span className="skeleton"></span>
            </div>
          : failed
            ? <div className="pl-empty">
                <div className="pl-empty-t">The list could not be run</div>
                <div className="pl-empty-s">
                  The player pool did not answer. Your criteria are untouched — running the list
                  again is usually enough.
                </div>
                <div className="pl-empty-a">
                  <Btn variant="accent" onClick={()=>loadList(selected)}>Try again</Btn>
                </div>
              </div>
            : !hasList
              ? <div className="pl-empty">
                  <div className="pl-empty-t">No list selected</div>
                  <div className="pl-empty-s">
                    Choose one of your saved lists above. Its matching players land here as a
                    sortable table you can open and preview.
                  </div>
                </div>
              : resultCount===0
                ? <div className="pl-empty">
                    <div className="pl-empty-t">No players match this list</div>
                    <div className="pl-empty-s">
                      Every filter narrows the pool. Widening the age or height range, or adding a
                      second country, is usually enough to bring players back.
                    </div>
                    <div className="pl-empty-a">
                      <Btn variant="accent" onClick={()=>navigate('/list/'+selected)}>Edit this list</Btn>
                    </div>
                  </div>
                : <>
                    <div className="pl-count">
                      <span className="pl-count-n num">{resultCount}</span>
                      <span className="pl-count-l">
                        {plural(resultCount,'player matches','players match')} {listName}.
                      </span>
                    </div>

                    <div className="pl-table">
                      <DataTable
                        columns={columns}
                        data={queryRsults}
                        customStyles={tableStyles}
                        highlightOnHover
                        persistTableHead
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[25,50,100,200]}
                        noDataComponent={<div className="pl-empty-s">No players to show.</div>}
                      />
                    </div>
                  </>}
      </Panel>
    </div>
  )
}
